import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { supabaseWorker } from '@/integrations/supabase/workerClient';
import { BaseEntity } from '@/types/base-entities';
import { toast } from 'sonner';

// Helper: safely coerce unknown to array
const toArray = <T = unknown>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
const getErrorMessage = (e: unknown): string => (
  e instanceof Error ? e.message : (() => { try { return JSON.stringify(e); } catch { return String(e); } })()
);

// Frontend Type (camelCase) - совместимый с Location для обратной совместимости
export interface Location extends BaseEntity {
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  isActive: boolean;
}

// DB Type (snake_case) - работаем с rental_points согласно архитектуре
type DbRentalPoint = {
  id: string;
  name: string;
  address: string | null;
  phone: string | null;
  email: string | null;
  is_active: boolean;
  user_id: string;
  created_at: string;
};

// Mappers согласно STYLEGUIDE.md
const dbRentalPointToLocation = (dbItem: DbRentalPoint): Location => ({
  id: dbItem.id,
  name: dbItem.name,
  address: dbItem.address || undefined,
  phone: dbItem.phone || undefined,
  email: dbItem.email || undefined,
  isActive: dbItem.is_active,
  createdAt: new Date(dbItem.created_at),
  updatedAt: new Date(dbItem.created_at), // rental_points не имеет updated_at
  userId: dbItem.user_id,
});

const locationToInsertDb = (
  item: Partial<Omit<Location, 'id'>>,
  userId: string
): Omit<DbRentalPoint, 'id' | 'created_at'> => ({
  name: item.name!,
  address: item.address || null,
  phone: item.phone || null,
  email: item.email || null,
  is_active: item.isActive ?? true,
  user_id: userId,
});

const locationToUpdateDb = (
  item: Partial<Omit<Location, 'id'>>
): Partial<Omit<DbRentalPoint, 'id' | 'created_at' | 'user_id'>> => ({
  ...(item.name !== undefined && { name: item.name }),
  ...(item.address !== undefined && { address: item.address || null }),
  ...(item.phone !== undefined && { phone: item.phone || null }),
  ...(item.email !== undefined && { email: item.email || null }),
  ...(item.isActive !== undefined && { is_active: item.isActive }),
});

// The Hook - полностью переписан для работы с rental_points
export const useLocationsUnified = (
  filters?: Partial<Record<string, string | number | boolean | null | undefined>>
) => {
  const { user } = useAuth();
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  console.log('[useLocationsUnified] User:', user?.id, 'Filters:', filters);

  // Загрузка пунктов проката из rental_points
  const fetchLocations = async () => {
    if (!user) {
      setLocations([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Формируем запрос с пошаговыми .eq фильтрами вместо .match()
      let query = supabaseWorker
        .from('rental_points')
        .select('id, name, address, phone, email, is_active, user_id, created_at')
        .eq('user_id', user.id);

      if (filters) {
        for (const [key, value] of Object.entries(filters)) {
          if (value === undefined || value === null) continue;
          if (
            typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean'
          ) {
            query = query.eq(key, value);
          }
        }
      }

      const { data, error: fetchError } = await query.order('created_at', { ascending: false });

      if (fetchError) {
        throw new Error(`Failed to fetch rental points: ${getErrorMessage(fetchError)}`);
      }

      const mappedLocations = toArray<DbRentalPoint>(data).map(dbRentalPointToLocation);
      setLocations(mappedLocations);
      console.log('[useLocationsUnified] Loaded locations:', mappedLocations.length);
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Unknown error');
      setError(errorObj);
      console.error('[useLocationsUnified] Error fetching locations:', errorObj);
    } finally {
      setLoading(false);
    }
  };

  // Создание пункта проката
  const createLocation = async (locationData: Partial<Omit<Location, 'id'>>) => {
    if (!user) throw new Error('User not authenticated');

    setIsCreating(true);
    try {
      const dbData = locationToInsertDb(locationData, user.id);
      
      const { data, error: createError } = await supabaseWorker
        .from('rental_points')
        .insert([dbData])
        .select('id, name, address, phone, email, is_active, user_id, created_at')
        .single();

      if (createError) {
        throw new Error(`Failed to create rental point: ${getErrorMessage(createError)}`);
      }

      const newLocation = dbRentalPointToLocation(data as DbRentalPoint);
      await fetchLocations(); // Обновляем список
      toast.success(`Пункт проката "${locationData.name}" создан`);
      return newLocation;
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Unknown error');
      toast.error('Ошибка при создании пункта проката');
      throw errorObj;
    } finally {
      setIsCreating(false);
    }
  };

  // Обновление пункта проката
  const updateLocation = async (id: string, locationData: Partial<Omit<Location, 'id'>>) => {
    if (!user) throw new Error('User not authenticated');

    setIsUpdating(true);
    try {
      const updateData = locationToUpdateDb(locationData);
      
      const { data, error: updateError } = await supabaseWorker
        .from('rental_points')
        .update(updateData)
        .eq('id', id)
        .eq('user_id', user.id)
        .select('id, name, address, phone, email, is_active, user_id, created_at')
        .single();

      if (updateError) {
        throw new Error(`Failed to update rental point: ${getErrorMessage(updateError)}`);
      }

      const updatedLocation = dbRentalPointToLocation(data as DbRentalPoint);
      await fetchLocations(); // Обновляем список
      return updatedLocation;
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Unknown error');
      throw errorObj;
    } finally {
      setIsUpdating(false);
    }
  };

  // Удаление пункта проката
  const deleteLocation = async (id: string) => {
    if (!user) throw new Error('User not authenticated');

    setIsDeleting(true);
    try {
      const { error: deleteError } = await supabaseWorker
        .from('rental_points')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (deleteError) {
        throw new Error(`Failed to delete rental point: ${getErrorMessage(deleteError)}`);
      }

      await fetchLocations(); // Обновляем список
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Unknown error');
      throw errorObj;
    } finally {
      setIsDeleting(false);
    }
  };

  // Загружаем данные при изменении пользователя или фильтров
  useEffect(() => {
    fetchLocations();
  }, [user, JSON.stringify(filters)]);

  return {
    locations,
    loading,
    error,
    refetch: fetchLocations,
    createLocation,
    updateLocation,
    deleteLocation,
    isCreating,
    isUpdating,
    isDeleting,
  };
};
